import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import * as FiIcons from 'react-icons/fi';
import { usePlaidLink } from 'react-plaid-link';

import SafeIcon from '../common/SafeIcon';
import { useClient } from '../context/ClientContext';
import { bankService } from '../services/bankService';
import supabase from '../supabase/supabase';

import ConnectBankModal from '../components/banking/ConnectBankModal';
import ReviewImportTransactionsModal from '../components/assistant/ReviewImportTransactionsModal';
import SearchableSelect from '../components/common/SearchableSelect';

import './ConnectBank.css';

const {
  FiRefreshCw,
  FiShield,
  FiLock,
  FiPlus,
  FiCreditCard,
  FiCheckCircle,
  FiAlertCircle,
  FiXCircle,
} = FiIcons;

export default function ConnectBank() {
  const { activeClient } = useClient();

  const organizationId =
    activeClient?.id || null;

  /*
   * Connected bank accounts.
   */
  const [
    connectedAccounts,
    setConnectedAccounts,
  ] = useState([]);

  /*
   * Loading states.
   */
  const [
    isLoadingAccounts,
    setIsLoadingAccounts,
  ] = useState(true);

  const [
    isPreparingPlaid,
    setIsPreparingPlaid,
  ] = useState(false);

  const [
    isSyncing,
    setIsSyncing,
  ] = useState(false);

  /*
   * Connect Bank modal.
   */
  const [
    isConnectModalOpen,
    setIsConnectModalOpen,
  ] = useState(false);

  /*
   * Plaid Link token.
   */
  const [
    linkToken,
    setLinkToken,
  ] = useState(null);

  /*
   * Currently syncing bank account.
   *
   * This is the INTERNAL:
   *
   * accounting.bank_accounts.id
   *
   * It is not the Plaid account ID.
   */
  const [
    syncingAccountId,
    setSyncingAccountId,
  ] = useState(null);

  /*
   * Currently disconnecting bank account.
   */
  const [
    removingAccountId,
    setRemovingAccountId,
  ] = useState(null);

  /*
   * General messages.
   */
  const [
    error,
    setError,
  ] = useState('');

  const [
    successMessage,
    setSuccessMessage,
  ] = useState('');

  /*
   * Chart of Accounts.
   */
  const [
    accountingAccounts,
    setAccountingAccounts,
  ] = useState([]);

  const [
    isLoadingAccountingAccounts,
    setIsLoadingAccountingAccounts,
  ] = useState(false);

  const [
    mappingAccountId,
    setMappingAccountId,
  ] = useState(null);

  /*
   * Review batch.
   */
  const [
    reviewBatchId,
    setReviewBatchId,
  ] = useState(null);

  const [
    isReviewModalOpen,
    setIsReviewModalOpen,
  ] = useState(false);

  /*
   * Keep track of the bank account the user
   * selected when starting the sync.
   *
   * This is useful for maintaining the user's
   * account-specific workflow even though Plaid
   * internally synchronizes the Item.
   */
  const [
    reviewBankAccountId,
    setReviewBankAccountId,
  ] = useState(null);

  /*
   * Approval state.
   */
  const [
    approvalLoading,
    setApprovalLoading,
  ] = useState(false);

  const [
    approvalResult,
    setApprovalResult,
  ] = useState(null);

  /*
   * Load connected bank accounts.
   */
  const fetchAccounts =
    useCallback(async () => {
      if (!organizationId) {
        setConnectedAccounts([]);
        setIsLoadingAccounts(false);
        return;
      }

      try {
        setIsLoadingAccounts(true);
        setError('');

        const accounts =
          await bankService.getConnectedBanks(
            organizationId
          );

        setConnectedAccounts(
          accounts || []
        );
      } catch (err) {
        console.error(
          'Failed to load connected bank accounts:',
          err
        );

        setError(
          err?.message ||
            'Unable to load your connected bank accounts.'
        );
      } finally {
        setIsLoadingAccounts(false);
      }
    }, [organizationId]);

  /*
   * Load active Chart of Accounts.
   */
  const fetchAccountingAccounts =
    useCallback(async () => {
      if (!organizationId) {
        setAccountingAccounts([]);
        return;
      }

      try {
        setIsLoadingAccountingAccounts(
          true
        );

        const accounts =
          await bankService.getActiveAccounts(
            organizationId
          );

        setAccountingAccounts(
          accounts || []
        );
      } catch (err) {
        console.error(
          'Failed to load accounting accounts:',
          err
        );

        setError(
          err?.message ||
            'Unable to load the organization Chart of Accounts.'
        );
      } finally {
        setIsLoadingAccountingAccounts(
          false
        );
      }
    }, [organizationId]);

  /*
   * Reset organization-specific review state
   * when the active organization changes.
   */
  useEffect(() => {
    setReviewBatchId(null);
    setReviewBankAccountId(null);
    setIsReviewModalOpen(false);
    setApprovalResult(null);
  }, [organizationId]);

  /*
   * Initial data loading.
   */
  useEffect(() => {
    fetchAccounts();
    fetchAccountingAccounts();
  }, [
    fetchAccounts,
    fetchAccountingAccounts,
  ]);

  /*
   * Create Plaid Link token.
   */
  const createLinkToken =
    useCallback(async () => {
      if (!organizationId) {
        throw new Error(
          'No active organization is selected. Please select an organization first.'
        );
      }

      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        throw sessionError;
      }

      if (!session?.access_token) {
        throw new Error(
          'Your session has expired. Please sign in again.'
        );
      }

      setIsPreparingPlaid(true);
      setError('');
      setSuccessMessage('');

      try {
        const {
          data,
          error: functionError,
        } =
          await supabase.functions.invoke(
            'plaid-create-link-token',
            {
              body: {
                organizationId,
              },
            }
          );

        if (functionError) {
          throw functionError;
        }

        if (
          !data?.success ||
          !data?.link_token
        ) {
          throw new Error(
            data?.error ||
              'Unable to prepare the secure Plaid connection.'
          );
        }

        setLinkToken(
          data.link_token
        );

        return data.link_token;
      } catch (err) {
        console.error(
          'Failed to create Plaid Link token:',
          err
        );

        setError(
          err?.message ||
            'Unable to connect to Plaid.'
        );

        throw err;
      } finally {
        setIsPreparingPlaid(false);
      }
    }, [organizationId]);

  /*
   * Run AI categorization for an import batch.
   */
  const categorizeImportBatch =
    useCallback(
      async (importBatchId) => {
        if (
          !organizationId ||
          !importBatchId
        ) {
          throw new Error(
            'Organization and import batch are required for AI categorization.'
          );
        }

        console.log(
          'Starting AI transaction categorization:',
          {
            organizationId,
            importBatchId,
          }
        );

        const {
          data,
          error: functionError,
        } =
          await supabase.functions.invoke(
            'ai-transaction-categorizer',
            {
              body: {
                organizationId,
                importBatchId,
              },
            }
          );

        if (functionError) {
          throw functionError;
        }

        if (!data?.success) {
          throw new Error(
            data?.error ||
              'Unable to categorize imported transactions.'
          );
        }

        console.log(
          'AI categorization result:',
          data
        );

        return data;
      },
      [organizationId]
    );

  /*
 * Process a newly-created import batch.
 *
 * Selected bank account
 *       ↓
 * Plaid transaction synchronization
 *       ↓
 * import_batches
 *       ↓
 * import_transactions
 *       ↓
 * AI categorization
 *       ↓
 * Review modal
 */
  const processImportedTransactions = async (
    transactionSync,
    bankAccountId
  ) => {
    const selectedStaged = Number(
      transactionSync?.selectedStaged ??
        transactionSync?.staged ??
        0
    );

    if (selectedStaged <= 0) {
      return null;
    }

    const importBatchId =
      transactionSync?.importBatchId || null;

    if (!importBatchId) {
      throw new Error(
        'Transactions were staged, but no import batch was returned.'
      );
    }

    /*
    * Make sure the selected bank account remains
    * the source context for this review.
    */
    setReviewBankAccountId(
      bankAccountId
    );

    /*
    * Run AI categorization before opening Review.
    */
    await categorizeImportBatch(
      importBatchId
    );

    /*
    * Store the batch that the Review modal should load.
    */
    setReviewBatchId(
      importBatchId
    );

    /*
    * Open Review after categorization completes.
    */
    setIsReviewModalOpen(true);

    return {
      bankAccountId,
      importBatchId,
      staged: selectedStaged,
      selectedStaged,
      duplicates: Number(
        transactionSync?.selectedDuplicates ??
          transactionSync?.duplicates ??
          0
      ),
    };
  };

  /*
   * Handle successful Plaid connection.
   *
   * IMPORTANT:
   *
   * Connecting Plaid does NOT synchronize
   * transactions automatically.
   *
   * Plaid accounts are synchronized first.
   *
   * The user chooses the specific connected
   * account later and clicks Sync.
   */
  const handlePlaidSuccess =
    useCallback(
      async (
        publicToken,
        metadata
      ) => {
        if (!organizationId) {
          setError(
            'No active organization is selected.'
          );
          return;
        }

        try {
          setError('');
          setSuccessMessage('');

          const {
            data: exchangeData,
            error: exchangeError,
          } =
            await supabase.functions.invoke(
              'plaid-exchange-token',
              {
                body: {
                  publicToken,
                  organizationId,
                  metadata:
                    metadata || {},
                },
              }
            );

          if (exchangeError) {
            throw exchangeError;
          }

          if (
            !exchangeData?.success ||
            !exchangeData?.item?.item_id
          ) {
            throw new Error(
              exchangeData?.error ||
                'The bank connection was not completed.'
            );
          }

          const plaidItemId =
            exchangeData.item.item_id;

          /*
           * Synchronize the connected Plaid
           * account records only.
           *
           * This does NOT import transactions.
           */
          const accountSync =
            await bankService.syncPlaidItem(
              plaidItemId,
              organizationId
            );

          if (
            !accountSync?.success
          ) {
            throw new Error(
              accountSync?.error ||
                'The bank connected successfully, but the accounts could not be synchronized.'
            );
          }

          await fetchAccounts();

          setSuccessMessage(
            'Your bank was connected successfully. Select the account you want to use, map it to a ProfitYO account, then click Sync.'
          );

          setIsConnectModalOpen(
            false
          );
        } catch (err) {
          console.error(
            'Plaid connection failed:',
            err
          );

          setError(
            err?.message ||
              'Unable to complete the bank connection.'
          );

          setIsConnectModalOpen(
            false
          );
        } finally {
          setLinkToken(null);
        }
      },
      [
        organizationId,
        fetchAccounts,
      ]
    );

  /*
   * Plaid Link.
   */
  const {
    open: openPlaid,
    ready: plaidReady,
  } = usePlaidLink({
    token: linkToken,

    onSuccess:
      handlePlaidSuccess,

    onExit: () => {
      setLinkToken(null);
      setIsConnectModalOpen(false);
    },
  });

  /*
   * Open Plaid once the token is ready.
   */
  useEffect(() => {
    if (
      linkToken &&
      plaidReady
    ) {
      setIsConnectModalOpen(false);
      openPlaid();
    }
  }, [
    linkToken,
    plaidReady,
    openPlaid,
  ]);

  /*
   * Open Connect Bank modal.
   */
  const handleConnectBank =
    () => {
      if (!organizationId) {
        setError(
          'No active organization is selected. Please select an organization first.'
        );
        return;
      }

      setError('');
      setSuccessMessage('');
      setIsConnectModalOpen(true);
    };

  /*
   * Map a bank account to a
   * ProfitYO Chart of Accounts account.
   */
  const handleAccountMapping =
    async (
      bankAccount,
      accountId
    ) => {
      if (
        !organizationId ||
        !bankAccount?.id ||
        !accountId
      ) {
        return;
      }

      try {
        setMappingAccountId(
          bankAccount.id
        );

        setError('');
        setSuccessMessage('');

        const updatedAccount =
          await bankService.updateBankAccountMapping(
            bankAccount.id,
            organizationId,
            accountId
          );

        setConnectedAccounts(
          (current) =>
            current.map((item) =>
              item.id ===
              bankAccount.id
                ? {
                    ...item,
                    ...updatedAccount,
                  }
                : item
            )
        );

        const selectedAccount =
          accountingAccounts.find(
            (account) =>
              account.id ===
              accountId
          );

        setSuccessMessage(
          `${
            bankAccount.account_name ||
            'Bank account'
          } mapped to ${
            selectedAccount?.name ||
            'the selected account'
          }. You can now sync this account.`
        );
      } catch (err) {
        console.error(
          'Failed to map bank account:',
          err
        );

        setError(
          err?.message ||
            'Unable to map the bank account.'
        );
      } finally {
        setMappingAccountId(
          null
        );
      }
    };

  /*
   * Synchronize a specific connected bank account
   * from the USER'S perspective.
   *
   * account.id
   *   = accounting.bank_accounts.id
   *
   * account.account_id
   *   = accounting.accounts.id
   *
   * account.plaid_item_id
   *   = external Plaid Item ID
   *
   * account.plaid_account_id
   *   = external Plaid Account ID
   *
   * The selected bank account remains the
   * source account.
   *
   * The suggested accounting category is
   * handled later by AI Review.
   *
   * IMPORTANT:
   *
   * The frontend sends the selected bankAccountId.
   *
   * The backend must not assume that Plaid
   * supports account_id on /transactions/sync.
   *
    * The production Edge Function retrieves
    * transactions for the selected Plaid account
    * and keeps that account as the user-facing
    * source context.
   */
  const handleSyncAccount =
    async (account) => {
      if (!organizationId) {
        setError(
          'No active organization is selected.'
        );
        return;
      }

      if (!account?.id) {
        setError(
          'The bank account could not be identified.'
        );
        return;
      }

      /*
       * The bank account must have a mapped
       * ProfitYO account before syncing.
       */
      if (!account.account_id) {
        setError(
          'Map this bank account to a ProfitYO account before synchronizing transactions.'
        );
        return;
      }

      try {
        setSyncingAccountId(
          account.id
        );

        setIsSyncing(true);

        setError('');
        setSuccessMessage('');

        /*
         * Save the selected account as the
         * user-facing sync context.
         */
        setReviewBankAccountId(
          account.id
        );

        console.log(
          'SYNCING SELECTED BANK ACCOUNT:',
          {
            bankAccountId:
              account.id,

            organizationId,

            /*
             * External Plaid identifiers.
             */
            plaidItemId:
              account.plaid_item_id,

            plaidAccountId:
              account.plaid_account_id,

            /*
             * Internal ProfitYO source account.
             */
            sourceAccountId:
              account.account_id,

            /*
             * The user-facing expectation.
             */
            selectedBankAccount:
              account.account_name ||
              account.institution_name ||
              'Bank Account',
          }
        );

        /*
         * Start synchronization using the
         * selected internal bank account ID.
         *
         * The Edge Function is responsible for
         * Plaid Item-level cursor handling.
         */
        const result =
          await bankService.syncBank(
            account.id,
            organizationId
          );

        console.log(
          'SELECTED BANK SYNC RESULT:',
          result
        );

        const transactionSync =
          result?.transactionSync || {};

        /*
         * Plaid may still be preparing
         * transaction data.
         */
        if (
          transactionSync.pending
        ) {
          setSuccessMessage(
            `${
              account.account_name ||
              'This bank account'
            } is still preparing transactions. Please sync again shortly.`
          );

          await fetchAccounts();

          return;
        }

        const staged = Number(
          transactionSync?.selectedStaged ??
            transactionSync?.staged ??
            0
        );

        /*
         * New transactions were staged.
         */
               if (staged > 0) {
          setSuccessMessage(
            `${staged} transaction${
              staged === 1
                ? ''
                : 's'
            } found for ${
              account.account_name ||
              account.institution_name ||
              'this bank account'
            }. Categorizing transactions...`
          );

          try {
            await processImportedTransactions(
              transactionSync,
              account.id
            );

            setSuccessMessage(
              `${staged} transaction${
                staged === 1
                  ? ''
                  : 's'
              } are ready for review.`
            );
          } catch (categorizationError) {
            console.error(
              'Transaction categorization failed:',
              categorizationError
            );

            setError(
              categorizationError?.message ||
                'Transactions were synchronized, but AI categorization failed.'
            );

            return;
          }

          await fetchAccounts();

          return;
        }

        /*
         * No new transactions.
         */
        setSuccessMessage(
          `${
            account.account_name ||
            account.institution_name ||
            'This bank account'
          } synchronized successfully. No new transactions were found.`
        );

        await fetchAccounts();
      } catch (syncError) {
        console.error(
          'Bank sync failed:',
          syncError
        );

        setError(
          syncError?.message ||
            'Failed to synchronize the bank account.'
        );
      } finally {
        setSyncingAccountId(
          null
        );

        setIsSyncing(false);
      }
    };

  /*
   * Called after the Review modal completes
   * the approval/import process.
   */
  const handleApprovalComplete =
    useCallback(
      async (result) => {
        console.log(
          'IMPORT APPROVAL COMPLETED:',
          result
        );

        setApprovalResult(
          result || null
        );

        setApprovalLoading(false);

        /*
         * Keep the result available briefly,
         * but the batch is no longer an active
         * review once everything was imported.
         */
        if (
          result?.success &&
          Number(
            result?.remainingCategorized ?? 0
          ) === 0
        ) {
          setReviewBatchId(null);
          setReviewBankAccountId(null);
          setIsReviewModalOpen(false);

          setSuccessMessage(
            result?.message ||
              `${Number(
                result?.importedCount ?? 0
              )} transaction${
                Number(
                  result?.importedCount ?? 0
                ) === 1
                  ? ''
                  : 's'
              } approved and imported successfully.`
          );

          await fetchAccounts();

          return;
        }

        /*
         * Partial approval/import.
         */
        setSuccessMessage(
          result?.message ||
            'The selected transactions were processed. Remaining transactions are still available for review.'
        );

        await fetchAccounts();
      },
      [fetchAccounts]
    );

  /*
   * Disconnect bank account.
   */
  const handleRemoveAccount =
    async (account) => {
      if (
        !organizationId ||
        !account?.id
      ) {
        return;
      }

      if (
        !window.confirm(
          `Disconnect ${
            account.account_name ||
            'this bank account'
          } from ProfitYO?`
        )
      ) {
        return;
      }

      try {
        setRemovingAccountId(
          account.id
        );

        setError('');
        setSuccessMessage('');

        await bankService.removeBank(
          account.id,
          organizationId
        );

        setConnectedAccounts(
          (current) =>
            current.filter(
              (item) =>
                item.id !==
                account.id
            )
        );

        /*
         * If the disconnected account was
         * the account currently associated
         * with the review flow, clear it.
         */
        if (
          reviewBankAccountId ===
          account.id
        ) {
          setReviewBankAccountId(null);
        }

        setSuccessMessage(
          'The bank account has been disconnected.'
        );
      } catch (err) {
        console.error(
          'Failed to disconnect bank account:',
          err
        );

        setError(
          err?.message ||
            'Unable to disconnect this bank account.'
        );
      } finally {
        setRemovingAccountId(
          null
        );
      }
    };

  /*
   * Currency formatting.
   */
  const formatCurrency =
    (value) =>
      new Intl.NumberFormat(
        undefined,
        {
          style: 'currency',
          currency:
            activeClient?.currency ||
            'USD',
        }
      ).format(
        Number(value || 0)
      );

  /*
   * Account type formatting.
   */
  const formatAccountType =
    (type) =>
      type
        ? type
            .replace(
              /_/g,
              ' '
            )
            .replace(
              /\b\w/g,
              (char) =>
                char.toUpperCase()
            )
        : 'Bank Account';

  /*
   * Last synchronization display.
   */
  const formatLastSync =
    (date) => {
      const parsedDate =
        date
          ? new Date(date)
          : null;

      return parsedDate &&
        !Number.isNaN(
          parsedDate.getTime()
        )
        ? `Last synced ${parsedDate.toLocaleString()}`
        : 'Not synchronized yet';
    };

  return (
    <div className="connect-bank-page">
      <div className="connect-bank-container">
        <div className="connect-bank-header">
          <div className="connect-bank-header-content">
            <div>
              <h1>
                Connect Bank
              </h1>

              <p>
                Securely connect your bank account to import and manage your banking activity in ProfitYO.
              </p>
            </div>

            <button
              type="button"
              className="connect-bank-primary-button"
              onClick={
                handleConnectBank
              }
              disabled={
                !organizationId ||
                isPreparingPlaid
              }
            >
              <SafeIcon
                icon={FiPlus}
              />

              Connect Bank
            </button>
          </div>
        </div>

        {error && (
          <div className="connect-bank-alert connect-bank-alert-error">
            <SafeIcon
              icon={FiAlertCircle}
            />

            <span>
              {error}
            </span>

            <button
              type="button"
              onClick={() =>
                setError('')
              }
              aria-label="Dismiss error"
            >
              <SafeIcon
                icon={FiXCircle}
              />
            </button>
          </div>
        )}

        {successMessage && (
          <div className="connect-bank-alert connect-bank-alert-success">
            <SafeIcon
              icon={FiCheckCircle}
            />

            <span>
              {successMessage}
            </span>

            <button
              type="button"
              onClick={() =>
                setSuccessMessage('')
              }
              aria-label="Dismiss success message"
            >
              <SafeIcon
                icon={FiXCircle}
              />
            </button>
          </div>
        )}

        <section className="connect-bank-security-card">
          <div className="connect-bank-security-icon">
            <SafeIcon
              icon={FiShield}
            />
          </div>

          <div>
            <h2>
              Your bank connection is secure
            </h2>

            <p>
              ProfitYO uses Plaid to securely connect to your financial institution. Your bank login credentials are handled directly by Plaid.
            </p>
          </div>

          <div className="connect-bank-security-lock">
            <SafeIcon
              icon={FiLock}
            />

            Secure connection
          </div>
        </section>

        <section className="connect-bank-accounts-section">
          <div className="connect-bank-section-header">
            <div>
              <h2>
                Connected Accounts
              </h2>

              <p>
                Bank accounts currently connected to this organization.
              </p>
            </div>

            <div
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
              }}
            >
              {reviewBatchId && (
                <button
                  type="button"
                  className="connect-bank-refresh-button"
                  onClick={() =>
                    setIsReviewModalOpen(true)
                  }
                  disabled={
                    isSyncing ||
                    approvalLoading
                  }
                >
                  Review Transactions
                </button>
              )}

              <button
                type="button"
                className="connect-bank-refresh-button"
                onClick={fetchAccounts}
                disabled={
                  isLoadingAccounts ||
                  isSyncing ||
                  approvalLoading
                }
              >
                <SafeIcon
                  icon={FiRefreshCw}
                />

                Refresh
              </button>
            </div>
          </div>

          {isLoadingAccounts ? (
            <div className="connect-bank-empty-state">
              <div className="connect-bank-loading-spinner" />

              <p>
                Loading connected accounts...
              </p>
            </div>
          ) : connectedAccounts.length ===
            0 ? (
            <div className="connect-bank-empty-state">
              <div className="connect-bank-empty-icon">
                <SafeIcon
                  icon={FiCreditCard}
                />
              </div>

              <h3>
                No connected accounts
              </h3>

              <p>
                Connect a bank account to start importing your banking activity.
              </p>

              <button
                type="button"
                className="connect-bank-primary-button"
                onClick={
                  handleConnectBank
                }
                disabled={
                  !organizationId
                }
              >
                <SafeIcon
                  icon={FiPlus}
                />

                Connect Bank
              </button>
            </div>
          ) : (
            <div className="connect-bank-account-list">
              {connectedAccounts.map(
                (account) => (
                  <div
                    key={account.id}
                    className="connect-bank-account-card"
                  >
                    <div className="connect-bank-account-icon">
                      <SafeIcon
                        icon={
                          FiCreditCard
                        }
                      />
                    </div>

                    <div className="connect-bank-account-details">
                      <div className="connect-bank-account-name">
                        {account.institution_name ||
                          'Bank'}

                        {account.mask && (
                          <span>
                            ••••{' '}
                            {
                              account.mask
                            }
                          </span>
                        )}
                      </div>

                      <div className="connect-bank-account-subtitle">
                        {account.account_name ||
                          'Bank Account'}
                      </div>

                      <div className="connect-bank-account-meta">
                        {formatAccountType(
                          account.account_type
                        )}

                        <span>
                          •
                        </span>

                        {formatLastSync(
                          account.last_sync_at
                        )}
                      </div>
                    </div>

                    <div className="connect-bank-account-mapping">
                      <label
                        htmlFor={`mapping-${account.id}`}
                      >
                        ProfitYO Account
                      </label>

                      <SearchableSelect
                        options={
                          accountingAccounts
                        }
                        value={
                          account.account_id ||
                          ''
                        }
                        onChange={(
                          accountId
                        ) =>
                          handleAccountMapping(
                            account,
                            accountId
                          )
                        }
                        placeholder="Select account"
                        searchPlaceholder="Search accounts..."
                        emptyMessage="No matching accounts"
                        disabled={
                          mappingAccountId ===
                            account.id ||
                          isLoadingAccountingAccounts
                        }
                        getGroup={(
                          accountingAccount
                        ) =>
                          accountingAccount.type ||
                          'Other'
                        }
                        getLabel={(
                          accountingAccount
                        ) =>
                          accountingAccount.code
                            ? `${accountingAccount.code} - ${accountingAccount.name}`
                            : accountingAccount.name
                        }
                        menuWidth={
                          320
                        }
                        menuMaxWidth={
                          400
                        }
                      />
                    </div>

                    <div className="connect-bank-account-balance">
                      <span>
                        Balance
                      </span>

                      <strong>
                        {formatCurrency(
                          account.balance
                        )}
                      </strong>
                    </div>

                    <div className="connect-bank-account-actions">
                      <button
                        type="button"
                        className="connect-bank-account-sync"
                        onClick={() =>
                          handleSyncAccount(
                            account
                          )
                        }
                        disabled={
                          syncingAccountId ===
                            account.id ||
                          removingAccountId ===
                            account.id ||
                          approvalLoading
                        }
                      >
                        <SafeIcon
                          icon={
                            FiRefreshCw
                          }
                        />

                        {syncingAccountId ===
                        account.id
                          ? 'Syncing...'
                          : 'Sync'}
                      </button>

                      <button
                        type="button"
                        className="connect-bank-account-remove"
                        onClick={() =>
                          handleRemoveAccount(
                            account
                          )
                        }
                        disabled={
                          syncingAccountId ===
                            account.id ||
                          removingAccountId ===
                            account.id ||
                          approvalLoading
                        }
                      >
                        {removingAccountId ===
                        account.id
                          ? 'Disconnecting...'
                          : 'Disconnect'}
                      </button>
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </section>

        <section className="connect-bank-how-it-works">
          <div>
            <h2>
              How it works
            </h2>

            <p>
              Your bank connection is handled securely through Plaid.
            </p>
          </div>

          <div className="connect-bank-steps">
            <div className="connect-bank-step">
              <span>
                1
              </span>

              <div>
                <strong>
                  Connect your bank
                </strong>

                <p>
                  Select your financial institution through Plaid.
                </p>
              </div>
            </div>

            <div className="connect-bank-step">
              <span>
                2
              </span>

              <div>
                <strong>
                  Secure authentication
                </strong>

                <p>
                  Sign in securely through your financial institution.
                </p>
              </div>
            </div>

            <div className="connect-bank-step">
              <span>
                3
              </span>

              <div>
                <strong>
                  Choose and sync an account
                </strong>

                <p>
                  Map the bank account you want to use, then click Sync to synchronize its banking activity.
                </p>
              </div>
            </div>
          </div>
        </section>

        <footer className="connect-bank-footer">
          <SafeIcon
            icon={FiShield}
          />

          <span>
            Bank credentials are handled securely by Plaid. ProfitYO does not see or store your bank password.
          </span>
        </footer>
      </div>

      {/* Connect Bank modal */}
      <ConnectBankModal
        isOpen={
          isConnectModalOpen
        }
        isLoading={
          isPreparingPlaid
        }
        onClose={() =>
          !isPreparingPlaid &&
          setIsConnectModalOpen(
            false
          )
        }
        onContinue={async () => {
          try {
            await createLinkToken();
          } catch {
            /*
             * Errors are handled by
             * createLinkToken.
             */
          }
        }}
      />

      {/*
       * Review Import Transactions modal.
       *
       * User-facing flow:
       *
       * Select BOA Checking
       *        ↓
       * Click Sync
       *        ↓
       * Plaid synchronization
       *        ↓
       * import_batches
       *        ↓
       * import_transactions
       *        ↓
       * AI categorization
       *        ↓
       * Review
       *
       * Source account:
       *     source_account_id
       *     = BOA / selected bank's
       *       mapped ProfitYO account
       *
       * Suggested account:
       *     suggested_account_id
       *     = AI/user accounting category
       *
       * These remain separate.
       */}
      <ReviewImportTransactionsModal
        isOpen={
          isReviewModalOpen
        }
        onClose={() => {
          setIsReviewModalOpen(false);
        }}
        organizationId={
          organizationId
        }
        importBatchId={
          reviewBatchId
        }
        organizationCurrency={
          activeClient?.currency ||
          'USD'
        }

        /*
         * The bank account that initiated
         * the current review workflow.
         *
         * This is the INTERNAL:
         *
         * accounting.bank_accounts.id
         *
         * The Review modal can use this later
         * to filter/display the selected source
         * account without confusing it with
         * suggested_account_id.
         */
        sourceBankAccountId={
          reviewBankAccountId
        }

        /*
         * Approval state.
         */
        approvalLoading={
          approvalLoading
        }

        approvalResult={
          approvalResult
        }

        onApprovalStart={() => {
          setApprovalLoading(true);
          setApprovalResult(null);
          setError('');
          setSuccessMessage('');
        }}

        onApprovalComplete={
          handleApprovalComplete
        }

        onApprovalError={(approvalError) => {
          console.error(
            'Import approval failed:',
            approvalError
          );

          setApprovalLoading(false);

          setError(
            approvalError?.message ||
              'Unable to approve and import the selected transactions.'
          );
        }}
      />
    </div>
  );
}